import React from 'react';
import { getSession, signIn } from 'next-auth/react';
import { FieldValues, useForm } from 'react-hook-form';
import { Alert, Button, Typography } from 'components/common';
import { InputCheck, InputEmail } from 'components/form';
import InputPassword from 'components/form/input-password/input-password';
import NextAuthProvidersEnum from 'const/auth';
import AppRoutes from 'const/routes';
import Logger from 'utils/logger';
import { useRouter } from 'next/router';
import { useNotification } from '@/hooks';
import {
	deleteLocalStorage,
	getLocalStorage,
	setLocalStorage,
} from '@/utils/localStorage';
import { LayoutLogin } from '@/components/layout';
import Link from 'next/link';
import { decrypt, encrypt } from '@/lib/crypto';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { verificationsService } from '@/api';

interface LawyerSignInProps {
	providers: NextAuthProvidersEnum[];
}

const Recaptcha = dynamic(
	() => import('@/components/common/recaptcha/ReCaptchaComponent'),
	{
		ssr: false,
	}
);

export const LawyerSignInScreen: React.FC<LawyerSignInProps> = () => {
	// Utils
	const router = useRouter();
	const { pathname, query } = router;
	const { toastError } = useNotification();
	const isStaging = process.env.NEXT_PUBLIC_SERVER === 'staging';
	const isDev = process.env.NEXT_PUBLIC_SERVER === 'develop';

	const logger = new Logger({ identifier: 'SignIn' });
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [errorMessage, setErrorMessage] = React.useState<
		string | React.ReactNode
	>();
	const [isRememberMe, setIsRememberMe] = React.useState<boolean>(false);
	const [recaptchaValid, setRecaptchaValid] = React.useState<boolean | null>(
		null
	);

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isValid },
	} = useForm({ mode: 'onSubmit' });

	// Inputs rules
	const rules = {
		email: {
			required: { value: true, message: 'This is required.' },
		},
		password: {
			required: { value: true, message: 'This is required.' },
		},
	};

	React.useEffect(() => {
		if (isStaging || isDev) {
			setRecaptchaValid(true);
		}
	}, [isStaging, isDev]);

	React.useEffect(() => {
		const rememberMe = getLocalStorage('lawyer_rememberme');
		const password = getLocalStorage('lawyer_password');
		const email = getLocalStorage('lawyer_email');
		setIsRememberMe(rememberMe);
		if (password && email && rememberMe) {
			setValue('rememberme', rememberMe, {
				shouldDirty: true,
				shouldValidate: true,
			});
			setValue('email', email, {
				shouldDirty: true,
				shouldValidate: true,
			});
			const passwordDescrypted = decrypt(password);
			setValue('password', passwordDescrypted, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	}, []);

	const deleteQuery = (): void => {
		delete query['verify-error'];
		delete query['callbackUrl'];
		delete query['error'];
		// remove verify-error
		router.replace({
			pathname,
			query,
		});
	};

	const handleShowErrors = (errorMessage: string): void => {
		const errorMsg = errorMessage || 'Error, try again';
		if (errorMessage.includes('verify')) {
			setErrorMessage(errorMsg);
		} else if (errorMessage.includes('reactivate')) {
			setErrorMessage(
				<Typography type="regular-16" className="!text-[#2D41F5]">
					Your account has been <b>deactivated</b> and you are unable to sign
					in. If you believe this is an error, please{' '}
					<b>contact your administrator</b> for assistance.
				</Typography>
			);
		} else {
			toastError(errorMsg);
		}
		deleteQuery();
	};

	const redirectBeforeLogin = async () => {
		const session = await getSession();
		if (!session?.user?.user?.profile_completion_date) {
			router.push(AppRoutes.LAWYERAPP_AUTH_SET_NEW_PASSWORD);
			return;
		} else if (!session.user.user.is_phone_number_verified) {
			router.push(AppRoutes.LAWYERAPP_AUTH_VERIFY_PHONE);
			return;
		} else if (session?.user?.user?.is_2fa_actived) {
			await verificationsService.verifyNumber('2fa');
			await router.push(AppRoutes.LAWYERAPP_AUTH_TWO_FACTOR);
			return;
		} else {
			router.push(AppRoutes.LAWYERAPP_DASHBOARD);
		}
	};

	const onChangeRecaptcha = () => {
		setRecaptchaValid(true);
	};

	// SUBMIT function
	const handleSubmitData = async (
		provider: NextAuthProvidersEnum,
		data?: FieldValues
	): Promise<void> => {
		setIsLoading(true);
		logger.info('Data: ', { data });
		setErrorMessage(undefined);
		try {
			data && setLocalStorage('temporal-password', data.password);
			setLocalStorage('lawyer_rememberme', data?.rememberme);
			if (data && data.rememberme) {
				const passwordEncrypted = encrypt(data.password);
				setLocalStorage('lawyer_password', passwordEncrypted);
				setLocalStorage('lawyer_email', data.email);
			}
			if (data && !data?.rememberme) {
				deleteLocalStorage('lawyer_password');
				deleteLocalStorage('lawyer_email');
			}
			if (provider === NextAuthProvidersEnum.CREDENTIALS) {
				const resSignIn = await signIn(
					NextAuthProvidersEnum.CREDENTIALS,
					{
						...data,
						redirect: false, // false -> need to show the errors retuned by backend
					},
					{
						basePath: '/api/auth-lawyer',
					}
				);

				// check errors
				if (resSignIn && resSignIn.status !== 200) {
					handleShowErrors(resSignIn.error || '');
				} else {
					redirectBeforeLogin();
				}
			}
		} catch (error) {
			logger.error(error);
			toastError(error as string);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<LayoutLogin title="Welcome" rol="lawyer">
			<form
				className="w-full"
				onSubmit={handleSubmit((data) =>
					handleSubmitData(NextAuthProvidersEnum.CREDENTIALS, data)
				)}
			>
				{/* Input - email */}
				<InputEmail
					dataCY="email"
					name="email"
					title="Email"
					placeholder="Email"
					register={register}
					rules={rules.email}
					error={errors.email}
					setValueCustom={setValue}
				/>

				{/* Input - password */}
				<InputPassword
					dataCY="password"
					name="password"
					title="Password"
					placeholder="Password"
					validate={false}
					register={register}
					rules={rules.password}
					error={errors.password}
					className="my-4"
				/>

				<div className="flex items-center justify-between w-full mb-6">
					<InputCheck
						name="rememberme"
						register={register}
						defaultChecked={isRememberMe}
					>
						Remember me
					</InputCheck>

					<Link href={AppRoutes.LAWYERAPP_AUTH_FORGOT_PASSWORD}>
						<Typography
							type="semibold-16"
							className="text-primary hover:opacity-60"
						>
							Forgot your password?
						</Typography>
					</Link>
				</div>

				{errorMessage ? <Alert text={errorMessage} className="mb-11" /> : null}

				<div className={clsx({ hidden: isStaging || isDev })}>
					<Recaptcha
						onChangeRecaptcha={onChangeRecaptcha}
						siteKey={process.env.NEXT_PUBLIC_API_RECAPTCHA || ''}
					/>
				</div>

				{/* Credentials */}
				<Button
					dataCY="signin"
					type="submit"
					size="full"
					decoration="primary"
					label="Sign In"
					loading={isLoading}
					disabled={isLoading || !isDirty || !isValid || !recaptchaValid}
					className="my-10"
				/>
			</form>
		</LayoutLogin>
	);
};
